import { clear, h } from "./dom.js";

export interface DialogChip {
  label: string;
  onPick: () => void;
}

export interface DialogCallbacks {
  onSubmit: (text: string) => void;
  onMic: () => void;
  onTapLine: () => void;
}

/**
 * RPG dialog box: name tag, typewriter line, the user's last words, quick-reply chips,
 * listening indicator and a text fallback. A fixed set of nodes is reused (no DOM growth).
 */
export class DialogView {
  readonly el: HTMLDivElement;
  private readonly name: HTMLDivElement;
  private readonly line: HTMLDivElement;
  private readonly you: HTMLDivElement;
  private readonly chips: HTMLDivElement;
  private readonly listening: HTMLSpanElement;
  private readonly bar: HTMLSpanElement;
  private readonly input: HTMLInputElement;
  private readonly mic: HTMLButtonElement;
  private typeTimer: ReturnType<typeof setInterval> | null = null;
  private listenTimer: ReturnType<typeof setInterval> | null = null;
  private fullText = "";

  constructor(host: HTMLElement, companionName: string, cb: DialogCallbacks) {
    this.name = h("div", { class: "name" }, companionName);
    this.you = h("div", { class: "you hidden" });
    this.line = h("div", { class: "line", "aria-live": "polite", onclick: () => cb.onTapLine() });
    this.chips = h("div", { class: "chips" });
    this.bar = h("span", { class: "bar", style: { width: "4em" } });
    this.listening = h("span", { class: "listening hidden", role: "status" }, "Słucham", this.bar);
    this.input = h("input", {
      type: "text",
      placeholder: "Napisz albo powiedz…",
      "aria-label": "Twoja odpowiedź",
      enterkeyhint: "send",
      maxlength: 500,
    });
    this.mic = h("button", { class: "primary", "aria-label": "Mów", onclick: () => cb.onMic() }, "Mów");
    const sendBtn = h("button", { "aria-label": "Wyślij", onclick: () => submit() }, "Wyślij");
    const submit = () => {
      const t = this.input.value.trim();
      if (!t) return;
      this.input.value = "";
      cb.onSubmit(t);
    };
    this.input.addEventListener("keydown", (e) => {
      if (e.key === "Enter") submit();
    });
    this.el = h(
      "div",
      { class: "dialog frame" },
      this.name,
      this.you,
      this.line,
      this.chips,
      h("div", { class: "row" }, this.listening, this.input, this.mic, sendBtn),
    );
    host.append(this.el);
  }

  setName(name: string): void {
    this.name.textContent = name;
  }

  /** Show a line with a typewriter effect (one interval, always cleared). */
  say(text: string): void {
    this.fullText = text;
    if (this.typeTimer) clearInterval(this.typeTimer);
    let i = 0;
    this.line.textContent = "";
    this.typeTimer = setInterval(() => {
      i = Math.min(this.fullText.length, i + 2);
      this.line.textContent = this.fullText.slice(0, i);
      if (i >= this.fullText.length && this.typeTimer) {
        clearInterval(this.typeTimer);
        this.typeTimer = null;
      }
    }, 30);
  }

  /** Finish the typewriter immediately. */
  flush(): void {
    if (this.typeTimer) clearInterval(this.typeTimer);
    this.typeTimer = null;
    this.line.textContent = this.fullText;
  }

  thinking(): void {
    if (this.typeTimer) clearInterval(this.typeTimer);
    this.typeTimer = null;
    this.fullText = "";
    this.line.textContent = "…";
  }

  showYou(text: string): void {
    this.you.textContent = text ? `Ty: ${text}` : "";
    this.you.classList.toggle("hidden", !text);
  }

  setChips(chips: DialogChip[]): void {
    clear(this.chips);
    for (const c of chips.slice(0, 6))
      this.chips.append(
        h(
          "button",
          {
            onclick: () => {
              this.setChips([]);
              c.onPick();
            },
          },
          c.label,
        ),
      );
  }

  /** Listening indicator with a draining bar. */
  setListening(on: boolean, ms = 8000): void {
    if (this.listenTimer) clearInterval(this.listenTimer);
    this.listenTimer = null;
    this.listening.classList.toggle("hidden", !on);
    this.mic.textContent = on ? "Stop" : "Mów";
    if (!on) return;
    const start = Date.now();
    const tick = () => {
      const left = Math.max(0, 1 - (Date.now() - start) / ms);
      this.bar.style.width = `${(left * 4).toFixed(2)}em`;
      if (left <= 0 && this.listenTimer) {
        clearInterval(this.listenTimer);
        this.listenTimer = null;
      }
    };
    tick();
    this.listenTimer = setInterval(tick, 250);
  }

  showPartial(text: string): void {
    this.showYou(text);
  }

  dispose(): void {
    if (this.typeTimer) clearInterval(this.typeTimer);
    if (this.listenTimer) clearInterval(this.listenTimer);
    this.el.remove();
  }
}
